import { useState } from "react";
import { useAdmin, LOGO_FONT_OPTIONS } from "@/context/AdminContext";
import IconPicker from "@/components/feature/IconPicker";
import type { LogoConfig } from "@/data/siteContent";

export default function LogoEditor({ isDark }: { isDark: boolean }) {
  const { logoConfig, setLogoConfig, getLogoFont } = useAdmin();
  const [draft, setDraft] = useState<LogoConfig>(logoConfig);
  const headingColor = isDark ? "text-white" : "text-gray-800";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const logoFont = getLogoFont();

  const save = () => {
    setLogoConfig(draft);
  };

  return (
    <div className={`rounded-2xl border p-5 ${cardBg}`}>
      <h3 className={`font-bold text-sm mb-1 ${headingColor}`}>
        <i className="ri-image-edit-line text-pink-400 mr-1.5"></i>
        Logo Design
      </h3>
      <p className={`text-xs mb-4 ${subColor}`}>
        Customize your logo icon, text, or switch to an image logo. Changes apply instantly across the navbar and footer.
      </p>

      {/* Mode toggle */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setDraft({ ...draft, mode: "icon-text" })}
          className={`flex-1 text-xs font-semibold py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
            draft.mode === "icon-text" ? "bg-pink-500 text-white" : isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          <i className="ri-font-size mr-1"></i>Icon + Text
        </button>
        <button
          onClick={() => setDraft({ ...draft, mode: "image" })}
          className={`flex-1 text-xs font-semibold py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
            draft.mode === "image" ? "bg-pink-500 text-white" : isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          <i className="ri-image-line mr-1"></i>Image Logo
        </button>
      </div>

      {draft.mode === "icon-text" ? (
        <div className="space-y-4">
          <div>
            <label className={`text-xs font-semibold mb-2 block ${subColor}`}>Icon</label>
            <div className="flex items-center gap-3">
              <IconPicker value={draft.icon} onChange={(icon) => setDraft({ ...draft, icon })} />
              <span className={`text-xs ${subColor}`}>{draft.icon}</span>
            </div>
          </div>
          <div>
            <label className={`text-xs font-semibold mb-2 block ${subColor}`}>Logo Text</label>
            <input
              type="text"
              value={draft.text}
              onChange={(e) => setDraft({ ...draft, text: e.target.value })}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 ${
                isDark ? "border-gray-600 bg-gray-700 text-white" : "border-gray-200 bg-white text-gray-800"
              }`}
            />
          </div>
          {/* Live preview */}
          <div className={`rounded-xl p-4 ${isDark ? "bg-gray-700/50" : "bg-gray-50"}`}>
            <div className={`text-xs font-semibold mb-2 ${subColor}`}>Live Preview</div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-pink-400">
                <i className={`${draft.icon} text-white text-sm`}></i>
              </div>
              <span className="font-bold text-xl" style={{ fontFamily: logoFont.fontFamily, ...logoFont.style }}>
                {draft.text}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className={`text-xs font-semibold mb-2 block ${subColor}`}>Logo Image URL</label>
            <input
              type="text"
              value={draft.imageUrl}
              onChange={(e) => setDraft({ ...draft, imageUrl: e.target.value })}
              placeholder="https://yourdomain.com/logo.png"
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-200 ${
                isDark ? "border-gray-600 bg-gray-700 text-white" : "border-gray-200 bg-white text-gray-800"
              }`}
            />
            <p className={`text-xs mt-1 ${subColor}`}>
              Paste a direct image URL. For best results, use a transparent PNG with a height of 32-64px.
            </p>
          </div>
          {draft.imageUrl && (
            <div className={`rounded-xl p-4 ${isDark ? "bg-gray-700/50" : "bg-gray-50"}`}>
              <div className={`text-xs font-semibold mb-2 ${subColor}`}>Live Preview</div>
              <img src={draft.imageUrl} alt="Logo preview" className="h-8 w-auto object-contain" />
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 mt-4">
        <button
          onClick={save}
          className="bg-green-500 hover:bg-green-600 text-white text-xs font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-check-line mr-1"></i>Save Logo
        </button>
        <button
          onClick={() => setDraft(logoConfig)}
          className={`text-xs cursor-pointer whitespace-nowrap transition-colors ${isDark ? "text-gray-400 hover:text-gray-200" : "text-gray-400 hover:text-gray-600"}`}
        >
          Reset
        </button>
      </div>
    </div>
  );
}